import { useEffect, useRef, useState } from 'react';

/**
 * useBotShield — layered anti-bot defense for public forms.
 *
 * Provides:
 *   - `honeypotProps` — spread onto the hidden <input> in {@link HoneypotField}
 *   - `HoneypotField` — the ready-to-render hidden component (renders it once
 *     inside every form). Off-screen positioning (not `display:none`) so
 *     bots that skip hidden fields still fall into the trap.
 *   - `getPayloadFields()` — returns `{ company_website, elapsed_ms,
 *     turnstile_token }` to spread into the submit body.
 *
 * Matches the server-side layers in `django_backend/core/bot_defense.py`
 * (Layer 1 honeypot, Layer 2 time-to-submit).
 */
export function useBotShield() {
  const [honeypot, setHoneypot] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const loadedAtRef = useRef(0);

  useEffect(() => {
    // Reset the mount timestamp each time the hook mounts (once per form
    // instance). Using a ref avoids re-renders on this internal value.
    loadedAtRef.current = Date.now();
  }, []);

  const getPayloadFields = () => ({
    company_website: honeypot,
    elapsed_ms: loadedAtRef.current ? Date.now() - loadedAtRef.current : 0,
    turnstile_token: turnstileToken || '',
  });

  const HoneypotField = () => (
    <div
      aria-hidden="true"
      style={{ position: 'absolute', left: '-9999px', top: 'auto', width: 1, height: 1, overflow: 'hidden', opacity: 0 }}
    >
      <label htmlFor="company_website">Company Website (leave blank)</label>
      <input
        type="text"
        id="company_website"
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
      />
    </div>
  );

  return {
    honeypot,
    HoneypotField,
    getPayloadFields,
    setTurnstileToken, // wire this to <Turnstile onSuccess={setTurnstileToken}/> when keys land
  };
}
